import fs from 'fs' // ssr
import path from 'path' // ssr
import clsx from 'clsx'
import { useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

import {
  Dash,
  Heading,
  Container,
  SubHeading,
  CopyButton,
  CustomImage,
  GridContainer,
  MainContainer,
  SectionContainer,
  NewsRecommendation,
  NewsBuilder,
} from '../../components/index'

import { CATEGORY_NEWS_SLUGS } from '../../utils/siteConfig'
import { formatDate, isObjectEmpty, generateNewsRecommendation } from '../../utils'
import getGlobalData from '../../services/cacheGlobalData'
import { getAllNews, getAcfNewsBySlug } from '../../services'
import useIntroEnded from '../../hooks/useIntroEnded'
import useLengthOfLongestWord from '../../hooks/useLengthOfLongestWord'

/**
 * Individual News Post
 */
const NewsPost = ({ recommendedNews, acfFields, newsCategory }) => (
  <MainContainer>
    {/* Core News Piece */}
    <NewsArticleCore
      date={acfFields?.date}
      heroHeading={acfFields?.title}
      copyButtonText='Copy the link to the article'
      heroImage={acfFields?.headerImage?.sourceUrl}
      heroImageAlt={acfFields?.headerImage?.altText}
      articleImage={acfFields?.contentImage?.sourceUrl}
      articleImageAlt={acfFields?.contentImage?.altText}
      articleImageCaption={acfFields?.contentImageCaption}
      contentBuilder={acfFields?.contentBuilder}
      newsCategory={newsCategory}
      headerLogo={acfFields?.headerLogo}
    />

    {/* Recommendation Section - OTHER UPDATES */}
    <NewsRecommendation news={recommendedNews} newsCategory={newsCategory} />
  </MainContainer>
)

export default NewsPost

// Pass article related data here from the main return(render) section
const NewsArticleCore = ({
  date,
  heroHeading,
  copyButtonText,
  heroImage,
  heroImageAlt,
  articleImage,
  articleImageAlt,
  articleImageCaption,
  contentBuilder,
  newsCategory,
  headerLogo,
}) => {
  const subHeadingRef = useRef()
  const headingRef = useRef()
  const figureRef = useRef()
  const tlH = useRef()
  const tl = useRef()
  const isCategoryNews = newsCategory === CATEGORY_NEWS_SLUGS.news
  const isCategoryCommunity = newsCategory === CATEGORY_NEWS_SLUGS.community
  const { introEnded } = useIntroEnded()

  useEffect(() => {
    ScrollTrigger.matchMedia({
      // desktop
      '(min-width: 768px)': function () {
        tl.current = gsap
          .timeline({
            scrollTrigger: {
              trigger: figureRef?.current,
              start: '0 100%',
              end: '100% 0',
              scrub: true,
              invalidateOnRefresh: true,
            },
          })
          .addLabel('init')
          .fromTo(
            figureRef?.current,
            {
              y: -100,
            },
            {
              y: 100,
            },
            'init'
          )

        return function () {
          tl?.current?.kill()
          gsap.set(figureRef?.current, { clearProps: true })
        }
      },
    })
  }, [])

  useEffect(() => {
    if (introEnded) {
      tlH.current = gsap
        .timeline({ defaults: { duration: 1, ease: 'bigd-ease-out' } })
        .addLabel('init')
        .call(() => subHeadingRef?.current?.animateIn(), null, 'init')
        .call(() => headingRef?.current?.animateIn(), null, 'init+=.3')
    }
  }, [introEnded])

  const hLengthMax = useLengthOfLongestWord(heroHeading)

  return (
    <SectionContainer className='hero-pt'>
      <h2 className='sr-only' aria-hidden='true'>
        News Detail Section
      </h2>
      <Container>
        <article>
          <GridContainer className='mb-35 md:mb-20'>
            <div className='col-span-7 md:col-start-1'>
              <SubHeading
                text={isCategoryCommunity ? ' ' : formatDate(date)}
                className='mb-40 text-white md:mb-60'
                ref={subHeadingRef}
                buildUpAnimation={true}
              />

              <div className={clsx('mb-30', isCategoryNews ? 'md:mb-0' : ' md:mb-120')}>
                {/* Article Heading */}
                <Heading
                  semantics='h1'
                  text={heroHeading}
                  className={clsx(
                    'tracking-4 text-3xl-B uppercase md:text-6xl-B',
                    hLengthMax > 10 ? 'h1-scale-down-A' : hLengthMax > 8 && 'h1-scale-down-B'
                  )}
                  buildUpAnimation={true}
                  ref={headingRef}
                />

                {isCategoryNews && (
                  <figure className='mt-70 w-150 border border-primary md:w-300'>
                    <CustomImage
                      alt={heroImageAlt}
                      src={heroImage}
                      aspectRatio='aspect-w-5 aspect-h-3'
                      className='object-fit object-cover'
                      imageClassName='bg-image'
                    />
                  </figure>
                )}
              </div>
            </div>
          </GridContainer>

          <GridContainer
            className={clsx(
              'mb-35 md:mb-20',
              isCategoryNews && 'border-b border-primary-light pb-12'
            )}>
            <div className='col-span-4 md:col-span-9'>
              <CopyButton text={copyButtonText} />
            </div>
          </GridContainer>

          {!isCategoryNews && (
            <GridContainer>
              <figure className='col-span-4 -mx-16 md:col-span-9 md:-mx-30'>
                <CustomImage
                  alt={heroImageAlt}
                  src={heroImage}
                  aspectRatio='aspect-w-5 aspect-h-3'
                  className='object-fit object-cover'
                  imageClassName='bg-image'
                />
              </figure>
            </GridContainer>
          )}

          {/* Page Content */}
          <GridContainer
            className={clsx(
              'gap-y-0 items-center pt-80',
              isCategoryNews ? 'md:pt-70 relative' : 'md:pt-150'
            )}>
            {/* Hide dash when there are images to be shown. Conflicts with images*/}
            {!(headerLogo?.sourceUrl || articleImage) && (
              <Dash className='hidden pb-20 md:absolute md:block md:pb-35 md:pt-0' />
            )}

            <div ref={figureRef} className='col-span-4 pr-15 md:col-span-2 md:pr-0'>
              <figure className='w-full'>
                {headerLogo ? (
                  <CustomImage
                    className='mb-6 w-full'
                    src={headerLogo.sourceUrl}
                    alt={headerLogo.altText}
                    objectProp={isCategoryNews && 'object-contain object-center'} // news -> logos
                  />
                ) : (
                  <CustomImage
                    className='mb-6 w-full bg-image object-cover'
                    src={articleImage}
                    alt={articleImageAlt}
                    aspectRatio='aspect-w-2 aspect-h-3'
                  />
                )}
              </figure>
              <figcaption className='text-right text-xs-caption -tracking-3 uppercase md:text-sm-A'>
                {articleImageCaption}
              </figcaption>
            </div>

            <div
              className={clsx(
                'col-span-4 md:col-span-5 md:col-start-3 md:pr-80',
                'row-start-1 md:row-start-auto'
              )}>
              {/* SECTION - Content Builder */}
              {isCategoryNews && <Dash className='absolute left-0 hidden md:block' />}
              <NewsBuilder data={contentBuilder}></NewsBuilder>
            </div>
          </GridContainer>
        </article>
      </Container>
    </SectionContainer>
  )
}

const allNewsPath = path.join(__dirname, 'temp_all_news_posts_cache.json')

/**
 * Generate/fetch all paths/slugs during the build time for SSR
 * @returns path
 */
export async function getStaticPaths() {
  const allNews = await getAllNews()

  // Save jobs to temporary file cache
  await fs.writeFileSync(allNewsPath, JSON.stringify(allNews), err => {
    if (err) console.log('News cache save error:', err)
  })

  const { allNewsPosts } = allNews
  const paths =
    allNewsPosts.map(news => ({
      params: {
        id: news?.id, // not being passed !!!
        slug: news?.slug,
      },
    })) || []

  return {
    paths,
    fallback: true, // if slug is not found in static site, it checks for new from backend on the fly
  }
}

/**
 * Fetch current page related data based on preview status & slug info
 * @param {*} param
 * @returns props
 */
export async function getStaticProps({ params, preview = false }) {
  let currentNews, allNews

  // load allNewsPosts from temporary file cache
  try {
    allNews = JSON.parse(fs.readFileSync(allNewsPath, 'utf8'))
  } catch (err) {
    console.log('News cache load error:', err)
  }

  // Preview Mode
  if (preview) {
    currentNews = await getAcfNewsBySlug(params?.slug) // Get preview data here
  } else {
    currentNews = allNews?.allNewsPosts?.filter(news => news?.slug === params?.slug)?.[0]
  }

  // Generate Random News for Recommendation
  // also factors-in the preview state
  const recommendedNews = generateNewsRecommendation(allNews, currentNews, preview)

  const globalData = await getGlobalData()
  const acfFields = {
    ...currentNews?.signaturePostNewsFields,
    date: currentNews?.date,
    title: currentNews?.title,
  }
  const pageData = { seo: currentNews?.seo }

  // Generate dynamic hrefs for community, magazines & press news items using their category
  const newsCategory = currentNews?.categories?.nodes?.[0]?.slug ?? null
  let pageType = 'newsArticle'
  let text
  let href

  if (newsCategory == CATEGORY_NEWS_SLUGS.community) {
    text = 'Back to Community'
    href = '/community#posts'
  } else {
    text = 'Back to news articles'
    href = '/media#news'
  }

  const additionalHeaderInfo = { pageType, text, href }

  return isObjectEmpty(currentNews)
    ? { notFound: true } // returns custom 404 page
    : {
        props: {
          preview,
          globalData,
          pageData,
          acfFields,
          recommendedNews,
          additionalHeaderInfo,
          newsCategory,
        },
      }
}
